import { useToast } from '@repo/design-system/index'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import { SummaryHighlightItem, SummaryHighlightTagResponse } from '../../../../../model/caf.interface'
import { useCafPinnedV5, useCafSummaryHighlightTagV5 } from '../../../../../modules/apis/webview/caf'
import useIsWebview from '../../../../../modules/hooks/common/useIsWebview'
import { HEADER_NAV_TAG_WORDING, getRoundValue } from '../../../../../modules/hooks/webview/caf/chart/utilFunction'
import useMapTagToWording from '../../../../../modules/hooks/webview/caf/common/useMapTagToWording'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { IC_CAF_PIN_INACTIVE, IC_PIN_PRIMARY_HIGHLIGHT } from '../../../../../modules/images'
import { COLORS } from '../../../../../modules/vars'
import { useLessonsStack } from '../../../../../pages/[lang]/webview/lessons'
import LoadingSpinner from '../../../../common/LoadingSpinner'
import HighlightedTitle from '../../../../new-portal/caf/common/HighlightedTitle'
import ThickDivider from '../../../../new-portal/caf/common/ThickDivider'
import RingleTip from '../../../../new-portal/caf/highlight/RingleTip'
import StatBarGraph from '../../../../new-portal/caf/statistic/StatBarGraph'
import HighlightListSection from '../../../../new-portal/caf/statistic/tag/TagHighlightListSection'
import WebviewStackContainer from '../../../common/WebviewStackContainer'

const AIStatisticHighlightDetailActivity = ({ params }) => {
  const { useLessonsFlow } = useLessonsStack()
  const { pop } = useLessonsFlow()
  const { tagId, startDate, endDate } = params
  const { t } = useTranslation()
  const { countWordingPerTag } = useMapTagToWording()
  const [highlights, setHighlights] = useState<SummaryHighlightItem[]>()
  const { data, isLoading, error, refetch, fetchNextPage, isFetchingNextPage } = useCafSummaryHighlightTagV5(+tagId, {
    startDate,
    endDate,
  })
  const { mutate } = useCafPinnedV5()
  const { toastSuccess } = useToast()
  const isWebview = useIsWebview()
  const [summaryHighlights, setSummaryHighlights] = useState<Partial<SummaryHighlightTagResponse>>()
  useEffect(() => {
    if (data?.pages?.length) {
      setHighlights(
        data.pages.reduce((prev: SummaryHighlightItem[], after: SummaryHighlightTagResponse) => {
          return prev.concat(after.highlights)
        }, []) as SummaryHighlightItem[]
      )
      setSummaryHighlights(data.pages[data.pages.length - 1])
      return
    }
  }, [data?.pages])
  const graphTitleList = {
    13: n_caf.statistic.highlight.graph_title13,
    14: n_caf.statistic.highlight.graph_title14,
    15: n_caf.statistic.highlight.graph_title15,
    else: n_caf.statistic.highlight.graph_title0, // 문법 0~12
  }

  const mutateResponse = (dt) =>
    dt?.map((item) => {
      return {
        ...item,
        level: item.count,
        tag: item.highlight_tag,
      }
    }) || []
  const avgCount = useMemo(() => {
    if (highlights?.length) {
      const sum = highlights.reduce((acc, cur) => acc + cur.count, 0)
      return sum / highlights.length
    }
  }, [highlights])

  const pinHighlight = (tag, isActive) => {
    mutate(
      { tag: tag, active: !isActive },
      {
        onSuccess: () => {
          if (!isActive) {
            toastSuccess(t(n_caf.highlight_list_edit.toast1))
          } else {
            toastSuccess(t(n_caf.highlight_list_edit.toast2))
          }
          refetch()
        },
      }
    )
  }

  return (
    <div>
      <WebviewStackContainer
        height="52px"
        onBack={pop}
        border={true}
        title={t(HEADER_NAV_TAG_WORDING[+tagId]) || ''}
        appendRight={() => (
          <Image
            src={summaryHighlights?.is_pinned ? IC_PIN_PRIMARY_HIGHLIGHT : IC_CAF_PIN_INACTIVE}
            width={31}
            height={31}
            alt="pin"
            className="fixed right-[17px] z-[999]"
            onClick={() => pinHighlight(tagId, summaryHighlights?.is_pinned)}
          />
        )}
      >
        {isLoading || !data ? (
          <div className="h-screen">
            <LoadingSpinner lg primary show />
          </div>
        ) : (
          <div className="keep-all min-h-screen break-words bg-[white] px-[20px] pt-[16px] pb-[40px] text-gray-700">
            <div className={styles.top.box}>
              <HighlightedTitle
                title={
                  avgCount > 0
                    ? t(countWordingPerTag(+tagId, getRoundValue(avgCount)).count)
                    : t(countWordingPerTag(+tagId).noCount)
                }
                color={COLORS.PURPLE50}
              />
              <span className={styles.top.subtitle}>{summaryHighlights?.component}</span>
            </div>
            <RingleTip tag={tagId} />
            {avgCount > 0 && (
              <article className={styles.graph.box}>
                <h2 className={styles.graph.top}>{t(graphTitleList[+tagId] ?? graphTitleList.else)}</h2>
                <StatBarGraph startDate={startDate} endDate={endDate} tag={tagId} />
                <div className={styles.bottom.title}>{t(n_caf.statistic.highlight.graph_date)}</div>
              </article>
            )}
            <ThickDivider />
            <HighlightListSection
              data={mutateResponse(highlights)}
              tag={+tagId}
              highlightsCount={summaryHighlights?.count}
              hasNext={summaryHighlights?.next}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
            />
          </div>
        )}
      </WebviewStackContainer>
    </div>
  )
}

export default AIStatisticHighlightDetailActivity

const styles = {
  top: {
    box: `mb-[16px] rounded-[6px] border border-gray-100 p-[16px]`,
    subtitle: `text-[12px] capitalize tracking-wide text-gray-500`,
  },
  graph: { box: `mb-[16px] `, top: `text-h5 mb-[12px] font-medium text-gray-700` },
  bottom: {
    title: `text-h5 text-center font-medium text-gray-700`,
  },
}
